import React, {useEffect, useState} from 'react';
import {Alert, Box, Card, MenuItem, Stack, TextField, Typography} from '@mui/material';
import API from "../../../utils/API";
import {API_ENDPOINTS} from "../../../apiConfig";
import Forms from "../../../components/Forms";
import Error from "../../../components/Error";

const defaultGateway = {
    enabled: false,
    endpointUrl: '',
    httpMethod: 'POST',
    authType: 'none',
    bearerToken: '',
    username: '',
    password: '',
    sender: '',
    recipientField: 'to',
    messageField: 'message',
    senderField: 'from',
    extraHeadersJson: '{}',
    extraBodyJson: '{}',
    successStatusCodes: '200,201,202',
    timeoutMs: 12000
};

const BUYBACK_CURRENCY_OPTIONS = ['CZK', 'EUR', 'USD'];

const Settings = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saved, setSaved] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [settings, setSettings] = useState({buybackCurrency: 'CZK', smsGateway: defaultGateway});
    const [testSms, setTestSms] = useState({to: '', text: 'Dobrý den, test SMS z AppleLab ERP.'});

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const {data} = await API.get(API_ENDPOINTS.appSettings.current);
            setSettings({
                ...data,
                buybackCurrency: data?.buybackCurrency || 'CZK',
                smsGateway: {...defaultGateway, ...(data?.smsGateway || {})}
            });
        } catch (e) {
            setError(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const updateGateway = (patch) => {
        setSettings((prev) => ({
            ...prev,
            smsGateway: {...prev.smsGateway, ...patch}
        }));
        setSaved(false);
    };

    const updateBuybackCurrency = (buybackCurrency) => {
        setSettings((prev) => ({...prev, buybackCurrency}));
        setSaved(false);
    };

    const save = async () => {
        setError(null);
        try {
            await API.put(API_ENDPOINTS.appSettings.update, {
                buybackCurrency: settings.buybackCurrency || 'CZK',
                smsGateway: settings.smsGateway
            });
            setSaved(true);
        } catch (e) {
            setError(e);
        }
    };

    const sendTestSms = async () => {
        setTestResult(null);
        setError(null);
        try {
            const {data} = await API.post(API_ENDPOINTS.sms.sendTest, testSms);
            setTestResult(data);
        } catch (e) {
            setError(e);
        }
    };

    if (loading) {
        return <Box className="p-4">Načítám nastavení...</Box>;
    }
    if (error) {
        return <Error error={error}/>;
    }

    const sms = settings.smsGateway || defaultGateway;

    return (
        <Box className="p-4 space-y-4">
            <Typography variant="h5">Nastavení aplikace</Typography>
            <Card className="p-4 space-y-4">
                <Typography variant="h6">Výkup</Typography>
                <Stack direction={{xs: 'column', md: 'row'}} spacing={2}>
                    <TextField
                        select
                        label="Výchozí měna výkupu"
                        size="small"
                        value={settings.buybackCurrency || 'CZK'}
                        onChange={(e) => updateBuybackCurrency(e.target.value)}
                        sx={{minWidth: 220}}
                    >
                        {BUYBACK_CURRENCY_OPTIONS.map((currency) => (
                            <MenuItem key={currency} value={currency}>{currency}</MenuItem>
                        ))}
                    </TextField>
                </Stack>
                <Stack direction="row" spacing={2}>
                    <Forms.Button label="Uložit nastavení" onClick={save}/>
                    {saved && <Alert severity="success">Uloženo</Alert>}
                </Stack>
            </Card>
            <Card className="p-4 space-y-4">
                <Typography variant="h6">SMS brána</Typography>
                <Stack direction={{xs: 'column', md: 'row'}} spacing={2}>
                    <TextField
                        select
                        label="Aktivní"
                        size="small"
                        value={sms.enabled ? 'yes' : 'no'}
                        onChange={(e) => updateGateway({enabled: e.target.value === 'yes'})}
                        sx={{minWidth: 180}}
                    >
                        <MenuItem value="yes">Ano</MenuItem>
                        <MenuItem value="no">Ne</MenuItem>
                    </TextField>
                    <TextField
                        label="Endpoint URL"
                        size="small"
                        fullWidth
                        value={sms.endpointUrl || ''}
                        onChange={(e) => updateGateway({endpointUrl: e.target.value})}
                    />
                </Stack>
                <Stack direction={{xs: 'column', md: 'row'}} spacing={2}>
                    <TextField
                        select
                        label="HTTP metoda"
                        size="small"
                        value={sms.httpMethod || 'POST'}
                        onChange={(e) => updateGateway({httpMethod: e.target.value})}
                        sx={{minWidth: 180}}
                    >
                        <MenuItem value="POST">POST</MenuItem>
                        <MenuItem value="GET">GET</MenuItem>
                    </TextField>
                    <TextField
                        select
                        label="Autentizace"
                        size="small"
                        value={sms.authType || 'none'}
                        onChange={(e) => updateGateway({authType: e.target.value})}
                        sx={{minWidth: 180}}
                    >
                        <MenuItem value="none">Žádná</MenuItem>
                        <MenuItem value="bearer">Bearer token</MenuItem>
                        <MenuItem value="basic">Basic auth</MenuItem>
                    </TextField>
                    <TextField
                        label="Sender"
                        size="small"
                        value={sms.sender || ''}
                        onChange={(e) => updateGateway({sender: e.target.value})}
                    />
                </Stack>
                {sms.authType === 'bearer' && (
                    <TextField
                        label="Bearer token"
                        size="small"
                        fullWidth
                        value={sms.bearerToken || ''}
                        onChange={(e) => updateGateway({bearerToken: e.target.value})}
                    />
                )}
                {sms.authType === 'basic' && (
                    <Stack direction={{xs: 'column', md: 'row'}} spacing={2}>
                        <TextField
                            label="Username"
                            size="small"
                            fullWidth
                            value={sms.username || ''}
                            onChange={(e) => updateGateway({username: e.target.value})}
                        />
                        <TextField
                            label="Password"
                            size="small"
                            type="password"
                            fullWidth
                            value={sms.password || ''}
                            onChange={(e) => updateGateway({password: e.target.value})}
                        />
                    </Stack>
                )}
                <Stack direction={{xs: 'column', md: 'row'}} spacing={2}>
                    <TextField
                        label="Pole příjemce"
                        size="small"
                        value={sms.recipientField || 'to'}
                        onChange={(e) => updateGateway({recipientField: e.target.value})}
                    />
                    <TextField
                        label="Pole zprávy"
                        size="small"
                        value={sms.messageField || 'message'}
                        onChange={(e) => updateGateway({messageField: e.target.value})}
                    />
                    <TextField
                        label="Pole odesílatele"
                        size="small"
                        value={sms.senderField || 'from'}
                        onChange={(e) => updateGateway({senderField: e.target.value})}
                    />
                    <TextField
                        label="Timeout (ms)"
                        size="small"
                        type="number"
                        value={sms.timeoutMs || 12000}
                        onChange={(e) => updateGateway({timeoutMs: Number(e.target.value) || 12000})}
                    />
                </Stack>
                <Stack direction={{xs: 'column', md: 'row'}} spacing={2}>
                    <TextField
                        label="Extra headers (JSON)"
                        size="small"
                        fullWidth
                        value={sms.extraHeadersJson || '{}'}
                        onChange={(e) => updateGateway({extraHeadersJson: e.target.value})}
                    />
                    <TextField
                        label="Extra payload (JSON)"
                        size="small"
                        fullWidth
                        value={sms.extraBodyJson || '{}'}
                        onChange={(e) => updateGateway({extraBodyJson: e.target.value})}
                    />
                    <TextField
                        label="Success status codes"
                        size="small"
                        value={sms.successStatusCodes || '200,201,202'}
                        onChange={(e) => updateGateway({successStatusCodes: e.target.value})}
                    />
                </Stack>
                <Stack direction="row" spacing={2}>
                    <Forms.Button label="Uložit nastavení" onClick={save}/>
                    {saved && <Alert severity="success">Uloženo</Alert>}
                </Stack>
            </Card>

            <Card className="p-4 space-y-3">
                <Typography variant="h6">Test SMS</Typography>
                <Stack direction={{xs: 'column', md: 'row'}} spacing={2}>
                    <TextField
                        label="Telefon"
                        size="small"
                        value={testSms.to}
                        onChange={(e) => setTestSms((prev) => ({...prev, to: e.target.value}))}
                    />
                    <TextField
                        label="Text SMS"
                        size="small"
                        fullWidth
                        value={testSms.text}
                        onChange={(e) => setTestSms((prev) => ({...prev, text: e.target.value}))}
                    />
                    <Forms.Button label="Odeslat test" onClick={sendTestSms}/>
                </Stack>
                {testResult && (
                    <Alert severity={testResult.sent ? 'success' : 'error'}>
                        {testResult.sent ? 'Test SMS odeslána' : 'Odeslání test SMS selhalo'}
                    </Alert>
                )}
            </Card>
        </Box>
    );
};

export default Settings;
